// src/pages/inventory/forecast/MonitoringStokBahanPG4.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    Chip,
    IconButton,
    MenuItem,
    Stack,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    Button,
    TextField,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import SearchIcon from '@mui/icons-material/Search';

import { useTheme } from "@mui/material/styles";
import '../../../components/css/ded-style.css';
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue, set, update } from "firebase/database";

// Import XLSX untuk handling file Excel
import * as XLSX from "xlsx";

// Import formatters dari file utilitas
import {
    excelDateToDDMMYYYY,
    formatNumber,
    formatDecimal,
    formatText
} from "../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../utils/formatLastUpdated";

// Import CustomTableContainer dan CustomBreadcrumbs dari komponen terpisah
import CustomTableContainer from "../../../components/CustomScrollContainer";
import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";

import RimenderStock from "../rimender-stock/RimenderStock";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper: Cek apakah nilai harus disimpan (tidak kosong dan bukan 0 atau tanda -)
const shouldSaveValue = (value) => {
    if (value === undefined || value === null || value === '') {
        return false;
    }
    
    // Jika berupa string, cek apakah kosong atau "0" atau "-"
    if (typeof value === 'string') {
        const trimmed = value.trim();
        
        // Cek berbagai format kosong/nol
        const isEmptyOrZero = trimmed === '' || 
                            trimmed === '0' || 
                            trimmed === '0.0' || 
                            trimmed === '0,0' ||
                            trimmed === '0.00' ||
                            trimmed === '0,00' ||
                            trimmed === '-' ||
                            trimmed === ' - ' ||
                            trimmed === '--' ||
                            trimmed === 'N/A' ||
                            trimmed === 'n/a' ||
                            trimmed.toLowerCase() === 'null' ||
                            trimmed.toLowerCase() === 'undefined';
        
        return !isEmptyOrZero;
    }
    
    // Jika berupa number, cek apakah bukan 0
    if (typeof value === 'number') {
        return value !== 0 && !isNaN(value);
    }
    
    // Untuk tipe data lain, simpan
    return true;
};

// Helper: Parse tanggal dari berbagai format ke DD-MM-YYYY
const parseDateToDDMMYYYY = (dateStr) => {
    if (!dateStr || dateStr === '') {
        console.log("dateStr kosong atau null");
        return null;
    }
    
    const normalizedStr = String(dateStr).trim();
    console.log("normalizedStr:", normalizedStr);
    
    // Skip jika adalah tanda -
    if (normalizedStr === '-' || normalizedStr === '--') {
        console.log("Skip karena tanda -");
        return null;
    }
    
    // Format DD/MM/YYYY (contoh: 01/12/2025)
    const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const slashMatch = normalizedStr.match(slashPattern);
    if (slashMatch) {
        const [_, day, month, year] = slashMatch;
        console.log("Match format DD/MM/YYYY");
        return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
    }
    
    // Format DD-MM-YYYY (sudah benar)
    const dashPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
    const dashMatch = normalizedStr.match(dashPattern);
    if (dashMatch) {
        const [_, day, month, year] = dashMatch;
        console.log("Match format DD-MM-YYYY");
        return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
    }
    
    // Coba parse dengan Date object (untuk format Excel serial date)
    try {
        console.log("Mencoba parse dengan excelDateToDDMMYYYY...");
        const excelDate = excelDateToDDMMYYYY(dateStr);
        console.log("Hasil excelDateToDDMMYYYY:", excelDate);
        if (excelDate && excelDate !== '-') {
            // Format excelDateToDDMMYYYY mengembalikan DD/MM/YYYY atau DD-MM-YYYY
            // Cek format dan konversi ke DD-MM-YYYY jika perlu
            
            // Jika sudah DD-MM-YYYY, langsung return
            const dashPattern = /^\d{2}-\d{2}-\d{4}$/;
            if (dashPattern.test(excelDate)) {
                console.log("Sudah format DD-MM-YYYY");
                return excelDate;
            }
            
            // Jika format DD/MM/YYYY, konversi ke DD-MM-YYYY
            const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
            const slashMatch = excelDate.match(slashPattern);
            if (slashMatch) {
                const [_, day, month, year] = slashMatch;
                console.log("Konversi dari DD/MM/YYYY ke DD-MM-YYYY");
                return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
            }
            
            // Return apa adanya jika sudah valid
            console.log("Menggunakan hasil dari excelDateToDDMMYYYY:", excelDate);
            return excelDate;
        }
    } catch (e) {
        console.warn("Gagal parse tanggal dengan excelDateToDDMMYYYY:", e);
    }
    
    // Coba parse sebagai Date object biasa (HANYA untuk string yang bukan number besar)
    try {
        // Skip jika adalah number Excel serial date (lebih dari 10000 biasanya)
        if (!isNaN(normalizedStr) && parseInt(normalizedStr) > 10000) {
            console.log("Skip parse sebagai Date object karena kemungkinan Excel serial date:", normalizedStr);
            return null;
        }
        
        console.log("Mencoba parse sebagai Date object...");
        const dateObj = new Date(normalizedStr);
        if (!isNaN(dateObj.getTime())) {
            const day = String(dateObj.getDate()).padStart(2, '0');
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const year = dateObj.getFullYear();
            console.log("Berhasil parse sebagai Date object");
            return `${day}-${month}-${year}`;
        }
    } catch (e) {
        console.warn("Gagal parse sebagai Date object:", e);
    }
    
    console.log("Tidak ada format yang cocok");
    return null;
};

// Fungsi untuk import Excel ke Firebase untuk stok bahan PG4 - SOLUSI 1
const importExcelToFirebaseStokBahan = async (file, database, onValuePromise) => {
    const reader = new FileReader();
    
    return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                // Gunakan raw: true untuk mendapatkan nilai mentah
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                    header: 1, 
                    raw: true,  // raw: true untuk mendapatkan nilai mentah
                    defval: '' 
                });
                
                // ========== DEBUG DETAILED EXCEL STRUCTURE ==========
                console.log("=== DEBUG EXCEL STRUCTURE ===");
                console.log("SheetNames:", workbook.SheetNames);
                console.log("Menggunakan sheet:", sheetName);
                console.log("Total rows in jsonData:", jsonData.length);
                
                // Tampilkan struktur beberapa baris pertama dengan detail
                console.log("Data Excel raw (10 baris pertama dengan detail):");
                for (let i = 0; i < Math.min(10, jsonData.length); i++) {
                    const row = jsonData[i];
                    console.log(`Row ${i} (length: ${row ? row.length : 0}):`);
                    if (row && Array.isArray(row)) {
                        for (let j = 0; j < Math.min(10, row.length); j++) {
                            const cellValue = row[j];
                            const columnLetter = String.fromCharCode(65 + j); // A, B, C, ...
                            console.log(`  ${columnLetter}${i + 1}:`, cellValue, `(type: ${typeof cellValue})`);
                        }
                    }
                }
                
                // Debug khusus untuk baris 2 (C3)
                console.log("=== DEBUG BARIS 2 (UNTUK C3) ===");
                const row2 = jsonData[2];
                if (row2 && Array.isArray(row2)) {
                    console.log("Baris 2 length:", row2.length);
                    for (let j = 0; j < row2.length; j++) {
                        const columnLetter = String.fromCharCode(65 + j);
                        console.log(`  ${columnLetter}3:`, row2[j], `(type: ${typeof row2[j]})`);
                    }
                } else {
                    console.log("Baris 2 tidak ada atau bukan array");
                }
                
                // Cek cell C3 langsung dari worksheet
                const cellC3 = worksheet['C3'];
                console.log("=== DEBUG CELL C3 LANGSUNG ===");
                console.log("worksheet['C3']:", cellC3);
                if (cellC3) {
                    console.log("cellC3.v (value):", cellC3.v);
                    console.log("cellC3.t (type):", cellC3.t);
                    console.log("cellC3.w (formatted):", cellC3.w);
                }
                // ========== END DEBUG ==========
                
                // Ambil tanggal update dari C3 (baris 2, kolom 2, 0-based index)
                let tanggalUpdate = '';
                if (jsonData.length > 2 && jsonData[2].length > 2) {
                    const rawTanggal = jsonData[2][2]; // C3
                    console.log("Nilai raw C3 dari jsonData[2][2]:", rawTanggal, `(type: ${typeof rawTanggal})`);
                    
                    tanggalUpdate = parseDateToDDMMYYYY(rawTanggal);
                    console.log("Hasil parse tanggal dari C3:", rawTanggal, "->", tanggalUpdate);
                    
                    // Jika masih null, coba dari worksheet langsung
                    if (!tanggalUpdate && cellC3 && cellC3.v) {
                        console.log("Coba parse dari worksheet['C3'].v:", cellC3.v);
                        tanggalUpdate = parseDateToDDMMYYYY(cellC3.v);
                        console.log("Hasil parse dari worksheet['C3'].v:", cellC3.v, "->", tanggalUpdate);
                    }
                }
                
                // Jika tanggal masih tidak valid, cari di sel sekitar
                if (!tanggalUpdate || tanggalUpdate === '-') {
                    console.log("=== MENCARI TANGGAL DI SEL SEKITAR C3 ===");
                    
                    // Daftar sel yang mungkin berisi tanggal
                    const potentialDateCells = [
                        'B3', 'D3', 'C2', 'C4', 'B2', 'D2', 'A3', 'E3'
                    ];
                    
                    for (const cellRef of potentialDateCells) {
                        const cell = worksheet[cellRef];
                        if (cell && cell.v) {
                            console.log(`Cek ${cellRef}:`, cell.v, `(type: ${typeof cell.v})`);
                            const parsed = parseDateToDDMMYYYY(String(cell.v));
                            if (parsed) {
                                tanggalUpdate = parsed;
                                console.log(`Tanggal ditemukan di ${cellRef}:`, tanggalUpdate);
                                break;
                            }
                        }
                    }
                }
                
                // Fallback: gunakan tanggal hari ini
                if (!tanggalUpdate || tanggalUpdate === '-') {
                    console.log("Menggunakan tanggal hari ini sebagai fallback");
                    const today = new Date();
                    const day = String(today.getDate()).padStart(2, '0');
                    const month = String(today.getMonth() + 1).padStart(2, '0');
                    const year = today.getFullYear();
                    tanggalUpdate = `${day}-${month}-${year}`;
                    console.log("Tanggal update (fallback):", tanggalUpdate);
                }
                
                // Validasi format DD-MM-YYYY
                const datePattern = /^\d{2}-\d{2}-\d{4}$/;
                if (!datePattern.test(tanggalUpdate)) {
                    throw new Error(`Format tanggal tidak valid: "${tanggalUpdate}". Harus DD-MM-YYYY. Pastikan sel C3 berisi tanggal yang valid.`);
                }
                
                console.log("=== TANGGAL UPDATE FINAL ===");
                console.log("Tanggal yang akan digunakan:", tanggalUpdate);
                
                const dataStartRow = 7; // B8 (0-based index: 7)
                
                // Mapping kolom berdasarkan spesifikasi (hanya forecast saja)
                const columnMapping = {
                    item_code: 0,      // Kolom B (index 1)
                    
                    // Forecast Fresh Pineapple
                    forecast_fresh_pineapple_M1: 1,   // C
                    forecast_fresh_pineapple_M2: 2,   // D
                    forecast_fresh_pineapple_M3: 3,   // E
                    forecast_fresh_pineapple_M4: 4,   // F
                    forecast_fresh_pineapple_M5: 5,   // G
                    forecast_fresh_pineapple_M6: 6,   // H
                    
                    // Forecast Banana
                    forecast_banana_M1: 7,   // I
                    forecast_banana_M2: 8,   // J
                    forecast_banana_M3: 9,  // K
                    forecast_banana_M4: 10,  // L
                    forecast_banana_M5: 11,  // M
                    forecast_banana_M6: 12,  // N
                    
                    // Forecast Other Fresh Fruits
                    forecast_other_fresh_fruits_M1: 13,  // O
                    forecast_other_fresh_fruits_M2: 14,  // P
                    forecast_other_fresh_fruits_M3: 15   // Q
                };

                const stokRef = ref(database, "db_inventory/db_stok_bahan");
                const existingStokSnapshot = await onValuePromise(stokRef);
                const existingStokData = existingStokSnapshot.val() || {};

                const importTimestamp = new Date().toISOString();

                let importedCount = 0;
                let updatedCount = 0;
                let skippedCount = 0;
                let ignoredCount = 0;
                let errorCount = 0;

                // Helper untuk parsing angka dari Excel ke string dengan 4 digit desimal
                const parseNumberToStringForFirebase = (value) => {
                    if (!shouldSaveValue(value)) {
                        return null; // Tidak akan disimpan karena kosong/0/-
                    }
                    
                    try {
                        // Jika sudah string, coba parse
                        if (typeof value === 'string') {
                            const trimmed = value.trim();
                            
                            // Skip jika string kosong atau tanda khusus
                            if (trimmed === '' || trimmed === '-' || trimmed === '--' || 
                                trimmed === '0' || trimmed === '0.0' || trimmed === '0.00') {
                                return null;
                            }
                            
                            // Cek jika sudah format yang diinginkan (misal: "81212.3650")
                            const decimalPattern = /^\d+\.\d{4}$/;
                            if (decimalPattern.test(trimmed)) {
                                return trimmed; // Sudah format yang benar
                            }
                            
                            // Jika ada koma desimal, ganti dengan titik
                            let processedValue = trimmed;
                            
                            // Hapus semua pemisah ribuan (titik di Indonesia)
                            processedValue = processedValue.replace(/\./g, '');
                            
                            // Ganti koma desimal dengan titik
                            processedValue = processedValue.replace(/,/g, '.');
                            
                            // Parse ke number untuk formatting
                            const num = parseFloat(processedValue);
                            
                            if (!isNaN(num) && num !== 0) {
                                // Format ke string dengan 4 digit desimal
                                // Gunakan toFixed untuk memastikan 4 digit
                                const formatted = (Math.round(num * 10000) / 10000).toFixed(4);
                                return formatted; // Contoh: "81212.3650"
                            }
                        }
                        
                        // Jika number langsung dari Excel
                        if (typeof value === 'number') {
                            if (value === 0 || isNaN(value)) {
                                return null;
                            }
                            // Format ke string dengan 4 digit desimal
                            const formatted = (Math.round(value * 10000) / 10000).toFixed(4);
                            return formatted; // Contoh: "81212.3650"
                        }
                        
                        return null;
                    } catch (error) {
                        console.warn("Gagal parse number:", value, error);
                        return null;
                    }
                };

                // Proses data dari baris 8 (B8) ke bawah
                for (let i = dataStartRow; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (!row || row.length === 0) {
                        ignoredCount++;
                        continue;
                    }
                    
                    const itemCode = row[columnMapping.item_code];
                    
                    // Skip jika item_code kosong, 0, atau tanda -
                    if (!shouldSaveValue(itemCode)) {
                        ignoredCount++;
                        continue;
                    }

                    const formattedItemCode = String(itemCode).trim();
                    
                    // Helper untuk menambahkan data hanya jika perlu disimpan
                    const addIfNeeded = (obj, key, value) => {
                        const parsedValue = parseNumberToStringForFirebase(value);
                        if (parsedValue !== null) {
                            obj[key] = parsedValue; // Simpan sebagai STRING dengan 4 digit desimal
                            return true; // Data ditambahkan
                        }
                        return false; // Data tidak ditambahkan
                    };
                    
                    // Fungsi untuk cek apakah ada data di semua kolom C-Q (hanya forecast)
                    const hasAnyDataInColumnsCtoQ = () => {
                        // Cek semua kolom dari C (index 2) sampai Q (index 16)
                        for (let col = 2; col <= 16; col++) {
                            const value = row[col];
                            if (shouldSaveValue(value)) {
                                return true;
                            }
                        }
                        return false;
                    };
                    
                    // Cek apakah ada data di kolom C-Q
                    if (!hasAnyDataInColumnsCtoQ()) {
                        console.log(`Skip baris ${i + 1} (${formattedItemCode}): Semua data di kolom C-Q kosong/0/-`);
                        skippedCount++;
                        continue; // Jangan simpan item ini ke Firebase
                    }
                    
                    // Buat node data untuk tanggal tertentu
                    const nodeData = {
                        tanggal_update: tanggalUpdate,
                        last_imported_at: importTimestamp
                    };
                    
                    // Forecast Fresh Pineapple
                    const forecastFp = {};
                    let hasFpData = false;
                    hasFpData = addIfNeeded(forecastFp, 'M1', row[columnMapping.forecast_fresh_pineapple_M1]) || hasFpData;
                    hasFpData = addIfNeeded(forecastFp, 'M2', row[columnMapping.forecast_fresh_pineapple_M2]) || hasFpData;
                    hasFpData = addIfNeeded(forecastFp, 'M3', row[columnMapping.forecast_fresh_pineapple_M3]) || hasFpData;
                    hasFpData = addIfNeeded(forecastFp, 'M4', row[columnMapping.forecast_fresh_pineapple_M4]) || hasFpData;
                    hasFpData = addIfNeeded(forecastFp, 'M5', row[columnMapping.forecast_fresh_pineapple_M5]) || hasFpData;
                    hasFpData = addIfNeeded(forecastFp, 'M6', row[columnMapping.forecast_fresh_pineapple_M6]) || hasFpData;
                    
                    if (hasFpData) {
                        nodeData.forecast_fresh_pineapple = forecastFp;
                    }
                    
                    // Forecast Banana
                    const forecastBanana = {};
                    let hasBananaData = false;
                    hasBananaData = addIfNeeded(forecastBanana, 'M1', row[columnMapping.forecast_banana_M1]) || hasBananaData;
                    hasBananaData = addIfNeeded(forecastBanana, 'M2', row[columnMapping.forecast_banana_M2]) || hasBananaData;
                    hasBananaData = addIfNeeded(forecastBanana, 'M3', row[columnMapping.forecast_banana_M3]) || hasBananaData;
                    hasBananaData = addIfNeeded(forecastBanana, 'M4', row[columnMapping.forecast_banana_M4]) || hasBananaData;
                    hasBananaData = addIfNeeded(forecastBanana, 'M5', row[columnMapping.forecast_banana_M5]) || hasBananaData;
                    hasBananaData = addIfNeeded(forecastBanana, 'M6', row[columnMapping.forecast_banana_M6]) || hasBananaData;
                    
                    if (hasBananaData) {
                        nodeData.forecast_banana = forecastBanana;
                    }
                    
                    // Forecast Other Fresh Fruits
                    const forecastOther = {};
                    let hasOtherData = false;
                    hasOtherData = addIfNeeded(forecastOther, 'M1', row[columnMapping.forecast_other_fresh_fruits_M1]) || hasOtherData;
                    hasOtherData = addIfNeeded(forecastOther, 'M2', row[columnMapping.forecast_other_fresh_fruits_M2]) || hasOtherData;
                    hasOtherData = addIfNeeded(forecastOther, 'M3', row[columnMapping.forecast_other_fresh_fruits_M3]) || hasOtherData;
                    
                    if (hasOtherData) {
                        nodeData.forecast_other_fresh_fruits = forecastOther;
                    }
                    
                    try {
                        // SOLUSI 1: Pastikan root item memiliki struktur yang benar
                        const rootItemRef = ref(database, `db_inventory/db_stok_bahan/${formattedItemCode}`);
                        
                        // Cek apakah root item sudah ada
                        const rootItemSnapshot = await onValuePromise(rootItemRef);
                        const existingRootData = rootItemSnapshot.val() || {};
                        
                        // Update atau buat root item dengan struktur yang benar
                        const updatedRootData = {
                            ...existingRootData, // Pertahankan data yang sudah ada
                            item_code: formattedItemCode,
                            // Pastikan ada struktur forecast
                            forecast: existingRootData.forecast || {}
                        };
                        
                        // Simpan data root item
                        await set(rootItemRef, updatedRootData);
                        
                        // SOLUSI 1: Simpan forecast data di path yang benar
                        // Path: db_inventory/db_stok_bahan/[item_code]/forecast/[tanggal_update]
                        const forecastRef = ref(database, `db_inventory/db_stok_bahan/${formattedItemCode}/forecast/${tanggalUpdate}`);
                        
                        // Set data (akan membuat atau menimpa)
                        await set(forecastRef, nodeData);
                        
                        // Cek apakah item sudah ada untuk menghitung imported/updated
                        const existingItemData = existingStokData[formattedItemCode];
                        if (existingItemData && existingItemData.forecast && existingItemData.forecast[tanggalUpdate]) {
                            updatedCount++;
                        } else {
                            importedCount++;
                        }
                        
                        // Debug log untuk beberapa data pertama
                        if (importedCount + updatedCount < 2) {
                            console.log(`Data baris ${i + 1} (${formattedItemCode}):`, nodeData);
                            console.log(`Path Root Firebase: db_inventory/db_stok_bahan/${formattedItemCode}`);
                            console.log(`Path Forecast Firebase: db_inventory/db_stok_bahan/${formattedItemCode}/forecast/${tanggalUpdate}`);
                            console.log(`Raw Excel value (forecast_fresh_pineapple M1):`, row[columnMapping.forecast_fresh_pineapple_M1], 
                                `(type: ${typeof row[columnMapping.forecast_fresh_pineapple_M1]})`);
                            console.log(`Parsed value for Firebase:`, nodeData.forecast_fresh_pineapple?.M1, 
                                `(type: ${typeof nodeData.forecast_fresh_pineapple?.M1})`);
                            console.log(`Display value (with formatNumber):`, 
                                formatNumber(nodeData.forecast_fresh_pineapple?.M1));
                        }
                        
                    } catch (error) {
                        console.error(`Error baris ${i + 1}:`, error);
                        errorCount++;
                    }
                }

                console.log(`=== HASIL IMPORT ===`);
                console.log(`Baru: ${importedCount}`);
                console.log(`Update: ${updatedCount}`);
                console.log(`Skip (kosong/0/-): ${skippedCount}`);
                console.log(`Abaikan: ${ignoredCount}`);
                console.log(`Error: ${errorCount}`);
                console.log(`Total: ${importedCount + updatedCount}`);
                console.log(`Tanggal Update: ${tanggalUpdate}`);

                resolve({
                    success: true,
                    importedCount,
                    updatedCount,
                    skippedCount,
                    ignoredCount,
                    errorCount,
                    total: importedCount + updatedCount,
                    tanggalUpdate
                });

            } catch (error) {
                console.error("Gagal impor Excel:", error);
                reject({
                    success: false,
                    error: "Terjadi kesalahan saat mengimpor data dari Excel.",
                    details: error.message
                });
            }
        };

        reader.onerror = () => {
            reject({
                success: false,
                error: "Gagal membaca file."
            });
        };

        reader.readAsArrayBuffer(file);
    });
};

// Fungsi untuk refresh data setelah import
const refreshStokBahanData = async (database, onValuePromise, materialMasterMap) => {
    const stokRef = ref(database, "db_inventory/db_stok_bahan");
    const updatedStokSnapshot = await onValuePromise(stokRef);
    const updatedStokData = updatedStokSnapshot.val() || {};
    
    const updatedLoadedData = [];
    
    // Flatten data untuk ditampilkan dalam tabel
    Object.keys(updatedStokData).forEach(itemCode => {
        const itemData = updatedStokData[itemCode];
        
        // Skip jika itemData bukan object
        if (!itemData || typeof itemData !== 'object') {
            return;
        }
        
        // Ambil item_code dari root atau gunakan firebaseKey sebagai fallback
        const rootItemCode = itemData.item_code || itemCode;
        
        // Ambil nama produk dari Material Master
        const namaProduk = materialMasterMap[rootItemCode]?.nama_produk || '';
        
        // PERUBAHAN: Akses data dari node forecast
        const forecastData = itemData.forecast;
        
        if (forecastData) {
            // Iterasi semua key di forecastData (yang berisi tanggal_update)
            Object.keys(forecastData).forEach(tanggalKey => {
                const node = forecastData[tanggalKey];
                
                // Pastikan node memiliki tanggal_update (valid node)
                if (node && typeof node === 'object' && node.tanggal_update) {
                    updatedLoadedData.push({
                        firebaseKey: `${itemCode}_${tanggalKey}`, // firebaseKey = item_code + tanggal
                        item_code: rootItemCode,
                        nama_produk: namaProduk, // Tambahkan nama produk di sini
                        tanggal_update: node.tanggal_update || tanggalKey,
                        
                        // Forecast Fresh Pineapple
                        forecast_fp_M1: node.forecast_fresh_pineapple?.M1 || '',
                        forecast_fp_M2: node.forecast_fresh_pineapple?.M2 || '',
                        forecast_fp_M3: node.forecast_fresh_pineapple?.M3 || '',
                        forecast_fp_M4: node.forecast_fresh_pineapple?.M4 || '',
                        forecast_fp_M5: node.forecast_fresh_pineapple?.M5 || '',
                        forecast_fp_M6: node.forecast_fresh_pineapple?.M6 || '',
                        
                        // Forecast Banana
                        forecast_banana_M1: node.forecast_banana?.M1 || '',
                        forecast_banana_M2: node.forecast_banana?.M2 || '',
                        forecast_banana_M3: node.forecast_banana?.M3 || '',
                        forecast_banana_M4: node.forecast_banana?.M4 || '',
                        forecast_banana_M5: node.forecast_banana?.M5 || '',
                        forecast_banana_M6: node.forecast_banana?.M6 || '',
                        
                        // Forecast Other Fresh Fruits
                        forecast_other_M1: node.forecast_other_fresh_fruits?.M1 || '',
                        forecast_other_M2: node.forecast_other_fresh_fruits?.M2 || '',
                        forecast_other_M3: node.forecast_other_fresh_fruits?.M3 || '',
                        
                        last_imported_at: node.last_imported_at || ''
                    });
                }
            });
        }
    });
    
    return updatedLoadedData;
};

function MonitoringStokBahanPG4() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // State untuk last update time
    const [lastUpdated, setLastUpdated] = useState(null);

    // Filter states
    const [filterItemCode, setFilterItemCode] = useState('');

    // State untuk data referensi Material Master (sama seperti di RealisasiStokBahanPG4.jsx)
    const [materialMasterData, setMaterialMasterData] = useState({});

    // Fungsi untuk mengambil data Material Master dari Firebase
    const fetchMaterialMasterData = async () => {
        try {
            const materialMasterRef = ref(database, "db_inventory/db_master_data/tb_material_master");
            const materialMasterSnapshot = await onValuePromise(materialMasterRef);
            const materialMasterData = materialMasterSnapshot.val() || {};
            
            // Konversi ke object dengan kode_material sebagai key
            const materialMasterMap = {};
            Object.keys(materialMasterData).forEach(key => {
                const record = materialMasterData[key];
                const kodeMaterial = record.kode_material || key;
                materialMasterMap[kodeMaterial] = {
                    nama_produk: record.nama_produk || '',
                    deskripsi: record.deskripsi || ''
                };
            });
            
            setMaterialMasterData(materialMasterMap);
            return materialMasterMap;
        } catch (error) {
            console.error("Error fetching material master data:", error);
            return {};
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Ambil data Material Master terlebih dahulu (sama seperti di RealisasiStokBahanPG4.jsx)
                const materialMasterMap = await fetchMaterialMasterData();
                
                // Fetch data dari db_inventory/db_stok_bahan
                const stokRef = ref(database, "db_inventory/db_stok_bahan");
                const stokSnapshot = await onValuePromise(stokRef);
                const stokData = stokSnapshot.val();
                const loadedData = [];
                let latestUpdateTime = null;
                
                if (stokData) {
                    // Flatten data untuk ditampilkan dalam tabel
                    Object.keys(stokData).forEach(itemCode => {
                        const itemData = stokData[itemCode];
                        
                        // PERUBAHAN: Akses data dari node forecast
                        const forecastData = itemData.forecast;
                        
                        if (forecastData) {
                            // Iterasi semua node tanggal di dalam forecast
                            Object.keys(forecastData).forEach(tanggalKey => {
                                const node = forecastData[tanggalKey];

                                if (node && typeof node === 'object' && node.tanggal_update) {
                                    // Ambil nama produk dari Material Master
                                    const namaProduk = materialMasterMap[itemCode]?.nama_produk || '';
                                    
                                    loadedData.push({
                                        firebaseKey: `${itemCode}_${tanggalKey}`,
                                        item_code: itemCode,
                                        nama_produk: namaProduk, // Tambahkan nama produk di sini
                                        tanggal_update: node.tanggal_update || tanggalKey,
                                        
                                        // Forecast Fresh Pineapple
                                        forecast_fp_M1: node.forecast_fresh_pineapple?.M1 || '',
                                        forecast_fp_M2: node.forecast_fresh_pineapple?.M2 || '',
                                        forecast_fp_M3: node.forecast_fresh_pineapple?.M3 || '',
                                        forecast_fp_M4: node.forecast_fresh_pineapple?.M4 || '',
                                        forecast_fp_M5: node.forecast_fresh_pineapple?.M5 || '',
                                        forecast_fp_M6: node.forecast_fresh_pineapple?.M6 || '',
                                        
                                        // Forecast Banana
                                        forecast_banana_M1: node.forecast_banana?.M1 || '',
                                        forecast_banana_M2: node.forecast_banana?.M2 || '',
                                        forecast_banana_M3: node.forecast_banana?.M3 || '',
                                        forecast_banana_M4: node.forecast_banana?.M4 || '',
                                        forecast_banana_M5: node.forecast_banana?.M5 || '',
                                        forecast_banana_M6: node.forecast_banana?.M6 || '',
                                        
                                        // Forecast Other Fresh Fruits
                                        forecast_other_M1: node.forecast_other_fresh_fruits?.M1 || '',
                                        forecast_other_M2: node.forecast_other_fresh_fruits?.M2 || '',
                                        forecast_other_M3: node.forecast_other_fresh_fruits?.M3 || '',
                                        
                                        last_imported_at: node.last_imported_at || ''
                                    });
                                }
                                
                                // Cari waktu update terakhir
                                if (node.last_imported_at) {
                                    const updateTime = new Date(node.last_imported_at);
                                    if (!latestUpdateTime || updateTime > latestUpdateTime) {
                                        latestUpdateTime = updateTime;
                                    }
                                }
                            });
                        }
                    });
                    
                    // Set last updated time
                    if (latestUpdateTime) {
                        setLastUpdated(latestUpdateTime);
                    }
                }

                // Urutkan berdasarkan item_code dan tanggal_update
                loadedData.sort((a, b) => {
                    if (a.item_code !== b.item_code) {
                        return a.item_code.localeCompare(b.item_code);
                    }
                    return b.tanggal_update.localeCompare(a.tanggal_update); // Terbaru di atas
                });

                setOriginalDataList(loadedData);
                setDataList(loadedData);

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data stok bahan.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                (item.item_code && item.item_code.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (item.nama_produk && item.nama_produk.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (item.tanggal_update && item.tanggal_update.toLowerCase().includes(searchQuery.toLowerCase()))
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    // Handle refresh data setelah import
    const refreshDataAfterImport = async () => {
        try {
            // Refresh data Material Master terlebih dahulu
            const materialMasterMap = await fetchMaterialMasterData();
            
            const updatedData = await refreshStokBahanData(database, onValuePromise, materialMasterMap);
            
            // Urutkan data
            updatedData.sort((a, b) => {
                if (a.item_code !== b.item_code) {
                    return a.item_code.localeCompare(b.item_code);
                }
                return b.tanggal_update.localeCompare(a.tanggal_update);
            });
            
            return updatedData;
        } catch (error) {
            console.error("Error refreshing data:", error);
            throw error;
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleMenuClick = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            // Gunakan fungsi import khusus untuk stok bahan
            const result = await importExcelToFirebaseStokBahan(file, database, onValuePromise);
            
            if (result.success) {
                setSnackbar({
                    open: true,
                    message: `Impor selesai! ${result.importedCount} data baru, ${result.updatedCount} data diperbarui. Tanggal update: ${result.tanggalUpdate}`,
                    severity: "success",
                });
                
                // Set last updated time
                setLastUpdated(new Date());
                
                // Refresh data setelah import berhasil
                const finalData = await refreshDataAfterImport();
                
                setDataList(finalData);
                setOriginalDataList(finalData);
            }
        } catch (error) {
            console.error("Gagal impor Excel:", error);
            setSnackbar({
                open: true,
                message: error.error || "Terjadi kesalahan saat mengimpor data dari Excel.",
                severity: "error",
            });
        } finally {
            event.target.value = null;
            handleMenuClose();
            setImportLoading(false);
        }
    };

    const handleExport = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handleToggleFilterPanel = () => {
        setShowFilterPanel(prev => !prev);
    };

    const handleFilterData = () => {
        let filtered = [...originalDataList];

        if (filterItemCode) {
            filtered = filtered.filter(item => item.item_code === filterItemCode);
        }

        setDataList(filtered);
        setPage(0);
    };

    const handleResetFilter = () => {
        setFilterItemCode('');
        setDataList(originalDataList);
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    // Get unique item codes for filter dropdown
    const itemCodeList = [...new Set(originalDataList.map(item => item.item_code))].filter(Boolean).sort();

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            {/*<RimenderStock />*/}

            {showFilterPanel && (
                <Paper sx={{ width: '100%', overflow: 'hidden', p: 2, mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                        Filter
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Item Code</InputLabel>
                            <Select
                                value={filterItemCode}
                                label="Item Code"
                                onChange={(e) => setFilterItemCode(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {itemCodeList.map((itemCode) => (
                                    <MenuItem key={itemCode} value={itemCode}>
                                        {itemCode}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleResetFilter}>
                            Reset Filter
                        </Button>
                        <Button variant="contained" onClick={handleFilterData}>
                            Filter Data
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Forecast Bahan PG4
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari item code, nama produk, atau tanggal..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        <IconButton color="primary" size="small" onClick={handleToggleFilterPanel}>
                            {showFilterPanel ? <FilterAltOutlinedIcon /> : <FilterAltOffOutlinedIcon />}
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="monitoring stok bahan table">
                                <TableHead>
                                    <TableRow className="tr">
                                        <TableCell className="ded-tch" rowSpan={2}>Item Code</TableCell>
                                        <TableCell className="ded-tch" rowSpan={2}>Nama Produk</TableCell>
                                        <TableCell className="ded-tch" rowSpan={2}>Tanggal<br/>Update</TableCell>
                                        
                                        {/* Forecast Fresh Pineapple */}
                                        <TableCell className="ded-tch" colSpan={6} align="center">
                                            Forecast Fresh Pineapple
                                        </TableCell>
                                        
                                        {/* Forecast Banana */}
                                        <TableCell className="ded-tch" colSpan={6} align="center">
                                            Forecast Banana
                                        </TableCell>
                                        
                                        {/* Forecast Other Fresh Fruits */}
                                        <TableCell className="ded-tch" colSpan={3} align="center">
                                            Forecast Other Fresh Fruits
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className="tr">
                                        
                                        {/* Sub-header untuk Forecast Fresh Pineapple */}
                                        <TableCell className="ded-tc">M1</TableCell>
                                        <TableCell className="ded-tc">M2</TableCell>
                                        <TableCell className="ded-tc">M3</TableCell>
                                        <TableCell className="ded-tc">M4</TableCell>
                                        <TableCell className="ded-tc">M5</TableCell>
                                        <TableCell className="ded-tc">M6</TableCell>
                                        
                                        {/* Sub-header untuk Forecast Banana */}
                                        <TableCell className="ded-tc">M1</TableCell>
                                        <TableCell className="ded-tc">M2</TableCell>
                                        <TableCell className="ded-tc">M3</TableCell>
                                        <TableCell className="ded-tc">M4</TableCell>
                                        <TableCell className="ded-tc">M5</TableCell>
                                        <TableCell className="ded-tc">M6</TableCell>
                                        
                                        {/* Sub-header untuk Forecast Other Fresh Fruits */}
                                        <TableCell className="ded-tc">M1</TableCell>
                                        <TableCell className="ded-tc">M2</TableCell>
                                        <TableCell className="ded-tc">M3</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => (
                                            <TableRow key={row.firebaseKey}>
                                                <TableCell className="ded-tbtrtc">
                                                    <Box>
                                                        <div style={{ fontWeight: 'bold' }}>
                                                            {formatText(row.item_code)}
                                                        </div>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <div>
                                                            {formatText(row.nama_produk)}
                                                        </div>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{formatText(row.tanggal_update)}</TableCell>
                                                
                                                {/* Forecast Fresh Pineapple */}
                                                <TableCell>{formatNumber(row.forecast_fp_M1)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_fp_M2)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_fp_M3)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_fp_M4)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_fp_M5)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_fp_M6)}</TableCell>
                                                
                                                {/* Forecast Banana */}
                                                <TableCell>{formatNumber(row.forecast_banana_M1)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_banana_M2)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_banana_M3)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_banana_M4)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_banana_M5)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_banana_M6)}</TableCell>
                                                
                                                {/* Forecast Other Fresh Fruits */}
                                                <TableCell>{formatNumber(row.forecast_other_M1)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_other_M2)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_other_M3)}</TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data monitoring stok bahan.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default MonitoringStokBahanPG4;